import {
  createDataFastWithAdapters,
  createHybridStorageAdapter,
  createMemoryStorageAdapter,
  getCookie,
  isFileProtocol,
  isInIframe,
  isLikelyBot,
  isLocalhostHostname,
  isValidEventName,
  isValidSessionId,
  type DataFastClient,
  type StorageAdapter,
} from 'datafast'
import { analyticsSafeUrl, safeAnalyticsParameters } from '../analyticsPrivacy'
import { isWebMeasurementAllowed } from '../measurementPolicy'
import { hasConsent } from './storage'

const DOMAIN = 'optioneer.io'
const PERSISTED_KEYS = new Set([
  'datafast_visitor_id',
  'datafast_session_id',
  'datafast_session_start',
  'datafast_visitor_first_seen_at',
  'datafast_visitor_session_count',
  'datafast_ignore',
])
type GoalFunction = ((goal: string, parameters?: Record<string, unknown>) => void) & {
  q?: ArrayLike<unknown>[]
}

let generation = 0
let initialization: Promise<void> | null = null
let client: DataFastClient | null = null
let operations = Promise.resolve()
let lastScreen: string | null = null
let removeDomTracking = () => {}
// Auth can resolve before consent or SDK readiness. Keep only the current ID in
// memory, then identify through the SDK once its consented visitor exists.
let authenticatedUserId: string | null = null
let identifiedUserId: string | null = null
let identityRevision = 0
let identityResetPending = false
const ACCOUNT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function allowed(): boolean {
  return (
    isWebMeasurementAllowed() &&
    hasConsent('analytics') &&
    !isLikelyBot() &&
    !isInIframe() &&
    !isLocalhostHostname() &&
    !isFileProtocol()
  )
}

function current(token: number): boolean {
  return token === generation && allowed()
}

function report(error: unknown): void {
  console.error('[Consent] DataFast: Failed to track', error)
}

function safePath(path: string): string {
  const safe = analyticsSafeUrl(path)
  const url = new URL(safe, window.location.origin)
  return `${url.pathname}${url.search}`
}

function currentPath(): string {
  return safePath(`${window.location.pathname}${window.location.search}`)
}

function removeLegacyStorage(): void {
  try {
    window.sessionStorage.removeItem('datafast_pageview_state')
  } catch {
    // An unavailable store cannot receive new pageview state from this adapter.
  }
  try {
    window.localStorage.removeItem('datafast_event_queue')
  } catch {
    // This adapter keeps the SDK's event queue in memory.
  }
}

/** Persist anonymous IDs only. In particular, SDK payload/referrer queues stay in memory. */
function storageFor(token: number): StorageAdapter {
  const persistent = createHybridStorageAdapter(DOMAIN)
  const memory = createMemoryStorageAdapter()
  return {
    async getItem(key) {
      if (!current(token)) return null
      if (!PERSISTED_KEYS.has(key)) return memory.getItem(key)
      // Explicit current consent supersedes the vendor's previous cleanup flag.
      if (key === 'datafast_ignore') return null
      // The SDK's hybrid read can migrate IDs after an await. Read synchronously
      // here so an obsolete initialization cannot overwrite a new client's cookies.
      try {
        const stored = getCookie(key) ?? window.localStorage.getItem(key)
        if (
          !stored &&
          key === 'datafast_session_start' &&
          isValidSessionId(getCookie('datafast_session_id') || '')
        ) {
          // The script tracker used the session cookie's expiry, without a start
          // timestamp. Preserve that still-active session during the SDK migration.
          const start = String(Date.now())
          await persistent.setItem(key, start)
          return current(token) ? start : null
        }
        return stored
      } catch {
        return memory.getItem(key)
      }
    },
    async setItem(key, value) {
      if (!current(token)) return
      await (PERSISTED_KEYS.has(key) ? persistent : memory).setItem(key, value)
    },
    async removeItem(key) {
      if (!current(token)) return
      await (PERSISTED_KEYS.has(key) ? persistent : memory).removeItem(key)
    },
  }
}

function enqueue(operation: (active: DataFastClient, token: number) => Promise<void>): void {
  if (!allowed() || !initialization) return
  const token = generation
  const revision = identityRevision
  const ready = initialization
  operations = operations
    .then(async () => {
      await ready
      if (!current(token) || revision !== identityRevision || !client) return
      const active = client
      await synchronizeIdentity(active, token)
      if (current(token) && revision === identityRevision) await operation(active, token)
    })
    .catch(report)
}

async function synchronizeIdentity(active: DataFastClient, token: number): Promise<void> {
  while (current(token) && (identityResetPending || identifiedUserId !== authenticatedUserId)) {
    if (identityResetPending || identifiedUserId !== null) {
      // The vendor links identity to its visitor. Rotate on logout/account
      // switch so later visits cannot inherit the previous account's identity.
      const revision = identityRevision
      await active.reset()
      if (!current(token)) return
      identifiedUserId = null
      identityResetPending = revision !== identityRevision
      lastScreen = null
      if (identityResetPending) continue
    }
    const next = authenticatedUserId
    if (next !== null) {
      await active.identify(next)
      if (!current(token)) return
      identifiedUserId = next
    }
  }
}

/** Only the stable account UUID is permitted; profile properties are never accepted. */
export function setDataFastUser(userId: string | null): void {
  const next =
    isWebMeasurementAllowed() && typeof userId === 'string' && ACCOUNT_ID.test(userId)
      ? userId.toLowerCase()
      : null
  if (next === authenticatedUserId) return
  // Anonymous activity belongs to this same visitor's login journey. Cancel
  // queued operations only when leaving an account, not on its first login.
  if (authenticatedUserId !== null) {
    ++identityRevision
    identityResetPending = true
  }
  authenticatedUserId = next
  enqueue(async () => {})
}

function goalParameters(parameters?: Record<string, unknown>): Record<string, string> | undefined {
  if (!parameters || typeof parameters !== 'object' || Array.isArray(parameters)) return undefined
  const primitives = Object.fromEntries(
    Object.entries(parameters).filter(([, value]) =>
      ['string', 'number', 'boolean'].includes(typeof value)
    )
  ) as Record<string, string | number | boolean>
  return Object.fromEntries(
    Object.entries(safeAnalyticsParameters(primitives) || {})
      .slice(0, 10)
      .map(([key, value]) => [
        key
          .toLowerCase()
          .replace(/[^a-z0-9_-]/g, '_')
          .slice(0, 32),
        String(value).slice(0, 255),
      ])
      .filter(([key]) => key && key !== 'eventname')
  )
}

function trackGoal(goal: string, parameters?: Record<string, unknown>): void {
  if (
    !allowed() ||
    typeof goal !== 'string' ||
    !isValidEventName(goal) ||
    ['identify', 'payment'].includes(goal.toLowerCase())
  )
    return
  const path = currentPath()
  const safeParameters = goalParameters(parameters)
  enqueue(async (active, token) => {
    if (lastScreen !== path) {
      await active.trackScreen(path)
      if (!current(token)) return
      lastScreen = path
    }
    if (current(token)) await active.track(goal, safeParameters)
  })
}

function trackOutboundLink(anchor: HTMLAnchorElement): void {
  let destination: URL
  try {
    destination = new URL(anchor.href)
  } catch {
    return
  }
  if (!['http:', 'https:'].includes(destination.protocol)) return
  const rootDomain = (hostname: string) =>
    hostname
      .replace(/^www\./, '')
      .split('.')
      .slice(-2)
      .join('.')
  if (rootDomain(destination.hostname) === rootDomain(window.location.hostname)) return
  const url = analyticsSafeUrl(destination.href)
  const text = safeAnalyticsParameters({
    text: (anchor.textContent || '').trim().slice(0, 255),
  })?.text
  const path = currentPath()
  enqueue(async (active, token) => {
    if (lastScreen !== path) {
      await active.trackScreen(path)
      if (!current(token)) return
      lastScreen = path
    }
    if (current(token)) await active.trackExternalLink(url, text)
  })
}

/** Initialize independently of GA, after the caller has saved analytics consent. */
export function initializeDataFast(): Promise<void> {
  if (!allowed()) return Promise.resolve()
  if (initialization) return initialization
  const token = ++generation
  const initialPath = currentPath()
  const datafastWindow = window as unknown as { datafast?: GoalFunction }
  const queued = Array.isArray(datafastWindow.datafast?.q) ? datafastWindow.datafast.q : []
  removeLegacyStorage()
  try {
    window.localStorage.removeItem('datafast_ignore')
  } catch {
    // Current consent still gates every operation and every storage write.
  }
  datafastWindow.datafast = trackGoal

  initialization = (async () => {
    const initialized = await createDataFastWithAdapters({
      appId: 'dfid_Fi0l3kuiumReMcuXCGJzK',
      domain: DOMAIN,
      platform: 'web',
      apiUrl: '/analytics/datafast/event',
      storage: storageFor(token),
      network: {
        async isConnected() {
          return current(token)
        },
      },
      maxQueueSize: 1,
      flushInterval: 5000,
    })
    if (!current(token)) {
      await initialized.optOut()
      return
    }
    client = initialized
    await initialized.trackScreen(initialPath)
    if (current(token)) lastScreen = initialPath
    await synchronizeIdentity(initialized, token)
  })().catch((error: unknown) => {
    if (token === generation) {
      ++generation
      const failed = client
      client = null
      initialization = null
      lastScreen = null
      identifiedUserId = null
      identityResetPending = false
      removeDomTracking()
      if (failed) void failed.optOut().catch(report)
    }
    report(error)
  })
  removeDomTracking = installDomTracking(token)
  for (const call of queued) {
    const [goal, parameters] = Array.from(call)
    if (typeof goal === 'string') trackGoal(goal, parameters as Record<string, unknown> | undefined)
  }
  return initialization
}

/** Capture the sanitized route now, so delayed operations retain their original page context. */
export function sendDataFastPageView(path: string): void {
  if (!allowed()) return
  const safe = safePath(path)
  enqueue(async (active, token) => {
    await active.trackScreen(safe)
    if (current(token)) lastScreen = safe
  })
}

function teardown(markIgnored: boolean): void {
  ++generation
  const previous = client
  client = null
  initialization = null
  operations = Promise.resolve()
  lastScreen = null
  identifiedUserId = null
  identityResetPending = false
  removeDomTracking()
  removeDomTracking = () => {}
  if (typeof window !== 'undefined') {
    ;(window as unknown as { datafast: GoalFunction }).datafast = () => {}
    removeLegacyStorage()
    if (markIgnored) {
      try {
        window.localStorage.setItem('datafast_ignore', 'true')
      } catch {
        // Current consent is checked again by the first-party event proxy.
      }
    }
  }
  // The generation-guarded storage prevents this old client touching newly accepted IDs.
  if (previous) void previous.optOut().catch(report)
}

export function cleanupDataFast(): void {
  teardown(true)
}

export function resetDataFastStateForTests(): void {
  authenticatedUserId = null
  ++identityRevision
  teardown(false)
}

/** Preserve the vendor's declarative goals without automatic URL capture or global API hooks. */
function installDomTracking(token: number): () => void {
  const observers = new Map<number, IntersectionObserver>()
  const seen = new WeakSet<Element>()
  const states = new WeakMap<
    Element,
    { fired: boolean; pending: ReturnType<typeof setTimeout> | null }
  >()
  const timers = new Set<ReturnType<typeof setTimeout>>()

  const activate = (event: MouseEvent | KeyboardEvent) => {
    if (!current(token)) return
    if (event instanceof KeyboardEvent && !['Enter', ' '].includes(event.key)) return
    const element =
      event.target instanceof Element ? event.target.closest('[data-fast-goal]') : null
    const goal = element?.getAttribute('data-fast-goal')?.trim()
    if (element && goal) {
      const parameters = Object.fromEntries(
        Array.from(element.attributes)
          .filter((attribute) => attribute.name.startsWith('data-fast-goal-'))
          .map((attribute) => [attribute.name.slice(15).replace(/-/g, '_'), attribute.value])
      )
      trackGoal(goal, parameters)
    }
    const anchor =
      event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null
    if (anchor) trackOutboundLink(anchor)
  }

  const observe = () => {
    if (!current(token) || typeof IntersectionObserver === 'undefined') return
    for (const element of document.querySelectorAll('[data-fast-scroll]')) {
      if (seen.has(element)) continue
      const parsed = Number.parseFloat(element.getAttribute('data-fast-scroll-threshold') || '')
      const threshold = Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 0.5
      if (!observers.has(threshold)) {
        observers.set(
          threshold,
          new IntersectionObserver(
            (entries) => {
              if (!current(token)) return
              for (const entry of entries) {
                const target = entry.target
                let state = states.get(target)
                if (!state) {
                  state = { fired: false, pending: null }
                  states.set(target, state)
                }
                if (!entry.isIntersecting || entry.intersectionRatio < threshold) {
                  if (state.pending !== null) {
                    clearTimeout(state.pending)
                    timers.delete(state.pending)
                    state.pending = null
                  }
                  state.fired = false
                  continue
                }
                if (state.fired || state.pending !== null) continue
                const parsedDelay = Number.parseInt(
                  target.getAttribute('data-fast-scroll-delay') || '',
                  10
                )
                const delay = Number.isFinite(parsedDelay) && parsedDelay >= 0 ? parsedDelay : 0
                const fire = () => {
                  if (state.pending !== null) timers.delete(state.pending)
                  state.pending = null
                  if (!current(token) || !target.isConnected) return
                  const bounds = target.getBoundingClientRect()
                  if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return
                  state.fired = true
                  const goal = target.getAttribute('data-fast-scroll')?.trim()
                  if (!goal) return
                  const height =
                    Math.max(
                      document.body.scrollHeight,
                      document.body.offsetHeight,
                      document.documentElement.clientHeight,
                      document.documentElement.scrollHeight,
                      document.documentElement.offsetHeight
                    ) - window.innerHeight
                  const offset = window.pageYOffset || document.documentElement.scrollTop
                  const parameters = Object.fromEntries(
                    Array.from(target.attributes)
                      .filter(
                        (attribute) =>
                          attribute.name.startsWith('data-fast-scroll-') &&
                          !['data-fast-scroll-threshold', 'data-fast-scroll-delay'].includes(
                            attribute.name
                          )
                      )
                      .map((attribute) => [
                        attribute.name.slice(17).replace(/-/g, '_'),
                        attribute.value,
                      ])
                  )
                  trackGoal(goal, {
                    scroll_percentage: String(
                      height <= 0 ? 100 : Math.min(100, Math.round((offset / height) * 100))
                    ),
                    threshold: String(threshold),
                    delay: String(delay),
                    ...parameters,
                  })
                }
                if (delay > 0) {
                  state.pending = setTimeout(fire, delay)
                  timers.add(state.pending)
                } else fire()
              }
            },
            { threshold: [0, threshold] }
          )
        )
      }
      observers.get(threshold)?.observe(element)
      seen.add(element)
    }
  }
  const mutation = typeof MutationObserver === 'undefined' ? null : new MutationObserver(observe)
  const start = () => {
    if (!current(token)) return
    observe()
    if (document.body) {
      mutation?.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-fast-scroll'],
      })
    }
  }
  document.addEventListener('click', activate)
  document.addEventListener('keydown', activate)
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start)
  else start()

  return () => {
    document.removeEventListener('click', activate)
    document.removeEventListener('keydown', activate)
    document.removeEventListener('DOMContentLoaded', start)
    mutation?.disconnect()
    observers.forEach((observer) => observer.disconnect())
    timers.forEach(clearTimeout)
  }
}
